import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import coachlogo from "../../assets/Images/full-logo.png";
import {
  LayoutDashboard,
  Users,
  X,
  Globe,
  Target,
  Award,
  DollarSign,
} from "lucide-react";
import { FaUserShield } from "react-icons/fa6";


const SideMenu = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const trigger = useRef(null);
  const sidebar = useRef(null);
  const [openProducts, setOpenProducts] = useState(false);
  const [openSpiritualInformation, setOpenSpiritualInformation] = useState(false);
  const [openBooking, setOpenBooking] = useState(false);

  const routeGroups = {
    dashboard: [`/dashboard`],
    users: [`/coachee`, `/coacheedetail`],
    coach: [`/coach`, `/coachdetail`],
    language: [`/language`],
    coachingSpecialities: [`/coachingspecialities`],
    coachingAccreditation: [`/coachingaccreditation`],
    currency: [`/currency`]
  };

  const menuItemClass = (groupKey) => {
    // Default dashboard to active if pathname is root or dashboard
    if (groupKey === "dashboard") {
      const isActive = (location.pathname === "/" || 
                       location.pathname === "/dashboard") &&
                       !location.pathname.startsWith("/coachee") &&
                       !location.pathname.startsWith("/coach") &&
                       !location.pathname.startsWith("/language") &&
                       !location.pathname.startsWith("/coachingspecialities") &&
                       !location.pathname.startsWith("/coachingaccreditation") &&
                       !location.pathname.startsWith("/currency"); 
      return isActive
        ? "flex items-center cursor-pointer bg-[#EAEFF6] text-gray-900 h-12 w-full px-4 rounded-xl"
        : "flex items-center cursor-pointer w-full px-4 h-12 text-white hover:bg-[#ffffff2c]/5 rounded-xl transition duration-200";
    }
    
    // For coachee menu item, check if pathname matches coachee or coacheedetail routes
    if (groupKey === "users") {
      const isActive = location.pathname === "/coachee" ||
                       location.pathname.startsWith("/coacheedetail");
      return isActive
        ? "flex items-center cursor-pointer bg-[#EAEFF6] text-gray-900 h-12 w-full px-4 rounded-xl"
        : "flex items-center cursor-pointer w-full px-4 h-12 text-white hover:bg-[#ffffff2c]/5 rounded-xl transition duration-200";
    }
    
    // For coach menu item, check if pathname matches coach routes (but not coachee routes)
    if (groupKey === "coach") {
      const isActive = location.pathname === "/coach" ||
                       location.pathname.startsWith("/coachdetail") ||
                       (location.pathname.startsWith("/coach/") && !location.pathname.startsWith("/coachee"));
    return isActive
        ? "flex items-center cursor-pointer bg-[#EAEFF6] text-gray-900 h-12 w-full px-4 rounded-xl"
        : "flex items-center cursor-pointer w-full px-4 h-12 text-white hover:bg-[#ffffff2c]/5 rounded-xl transition duration-200";
    }
    
    // For language menu item, check if pathname matches language route
    if (groupKey === "language") {
      const isActive = location.pathname === "/language" ||
                       location.pathname.startsWith("/language");
      return isActive
        ? "flex items-center cursor-pointer bg-[#EAEFF6] text-gray-900 h-12 w-full px-4 rounded-xl"
        : "flex items-center cursor-pointer w-full px-4 h-12 text-white hover:bg-[#ffffff2c]/5 rounded-xl transition duration-200";
    }

    if (groupKey === "coachingspecialities") {
      const isActive = location.pathname === "/coachingspecialities" ||
                       location.pathname.startsWith("/coachingspecialities");
      return isActive
        ? "flex items-center cursor-pointer bg-[#EAEFF6] text-gray-900 h-12 w-full px-4 rounded-xl"
        : "flex items-center cursor-pointer w-full px-4 h-12 text-white hover:bg-[#ffffff2c]/5 rounded-xl transition duration-200";
    }

    if (groupKey === "coachingaccreditation") {
      const isActive = location.pathname === "/coachingaccreditation" ||
                       location.pathname.startsWith("/coachingaccreditation");
      return isActive
        ? "flex items-center cursor-pointer bg-[#EAEFF6] text-gray-900 h-12 w-full px-4 rounded-xl"
        : "flex items-center cursor-pointer w-full px-4 h-12 text-white hover:bg-[#ffffff2c]/5 rounded-xl transition duration-200";
    }

    if (groupKey === "currency") {
      const isActive = location.pathname === "/currency" ||
                       location.pathname.startsWith("/currency");
      return isActive
        ? "flex items-center cursor-pointer bg-[#EAEFF6] text-gray-900 h-12 w-full px-4 rounded-xl"
        : "flex items-center cursor-pointer w-full px-4 h-12 text-white hover:bg-[#ffffff2c]/5 rounded-xl transition duration-200";
    }

      return "flex items-center cursor-pointer w-full px-4 h-12 text-white hover:bg-[#ffffff2c]/5 rounded-xl transition duration-200";
  };

  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  const navigateToProduct = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const navigateTo = (path) => {
    navigate(path);
    setOpenProducts(false);
    setOpenSpiritualInformation(false);
    setSidebarOpen(false);
  };

  return (
    <>
      <aside
        ref={sidebar}
        className={`fixed left-0 top-0 z-[999] flex h-screen w-80 md:w-[340px] flex-col bg-[#2C5BA7] shadow-lg transition-transform duration-300 ease-in-out dark:bg-boxdark lg:static lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="relative flex items-center justify-center border-b border-white/10 px-6 py-3">
        <div className="flex justify-center items-center h-full">
          <div
            onClick={() => navigate("")}
    className="cursor-pointer flex items-center gap-4"
          >
    <img src={coachlogo} className="h-14 w-auto object-contain" alt="Logo" />
    <p className="text-white font-bold text-lg text-center">Coach It Right Admin</p>
  </div>
          </div>

          <button
            ref={trigger}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
            className="absolute right-4 top-4 block text-white lg:hidden"
          >
            <X size={24} />
          </button>
        </div>

        <div className="no-scrollbar relative hidden-scrollbar flex flex-col justify-between overflow-y-auto px-4 sm:px-6 py-6 h-full">
          <div className="space-y-3">
            <div
              onClick={() => navigateTo(`/dashboard`)}
            >
              <span className={menuItemClass("dashboard")}>
                <LayoutDashboard size={20} />
                <h4 className="ml-3 fontNew text-sm sm:text-base font-medium">
                  Dashboard
                </h4>
              </span>
            </div>

            <div
              onClick={() => navigateTo(`/coachee`)}
            >
              <span className={menuItemClass("users")}>
                <Users size={20} />
                <h4 className="ml-3 fontNew text-sm sm:text-base font-medium">
                  Coachee
                </h4>
              </span>
            </div>

            <div
              onClick={() => navigateTo(`/coach`)}
            >
              <span className={menuItemClass("coach")}>
                <FaUserShield className="w-5 h-5" />
                <h4 className="ml-3 fontNew text-sm sm:text-base font-medium">
                  Coach
                </h4>
              </span>
            </div>

            <div
              onClick={() => navigateTo(`/language`)}
            >
              <span className={menuItemClass("language")}>
                <Globe size={20} />
                <h4 className="ml-3 fontNew text-sm sm:text-base font-medium">
                  Language
                </h4>
              </span>
            </div>


            <div
              onClick={() => navigateTo(`/coachingspecialities`)}
            >
              <span className={menuItemClass("coachingspecialities")}>
                <Target size={20} />
                <h4 className="ml-3 fontNew text-sm sm:text-base font-medium">
                Coaching Specialities
                </h4>
              </span>
            </div>

            <div
              onClick={() => navigateTo(`/coachingaccreditation`)}
            >
              <span className={menuItemClass("coachingaccreditation")}>
                <Award size={20} />
                <h4 className="ml-3 fontNew text-sm sm:text-base font-medium">
                Coaching Accreditation
                </h4>
              </span>
            </div>

            <div
              onClick={() => navigateTo(`/currency`)}
            >
              <span className={menuItemClass("currency")}>
                <DollarSign size={20} />
                <h4 className="ml-3 fontNew text-sm sm:text-base font-medium">
                Currency
                </h4>
              </span>
            </div>
            </div>
        </div>
      </aside>
    </>
  );
};

export default SideMenu;
